import { useState, useCallback, useRef } from "react"
import {
  useLazySearchTransfersQuery,
  useTransPrepareBookingMutation,
  useLazyGetTransferDatesQuery,
} from "../../app/services/transfer/search"
import { formatToApiDate } from "../utils/date/format"
import { DEFAULT_CURRENCY } from "../utils/variables/common"

const defaultDates = {
  depart: [],
  return: [],
}

const defaultPassengers = {
  adults: 1,
  children: 0,
  infants: 0,
}

// нормализует ключ dd.mm.yyyy → yyyy-mm-dd, остальные не трогает
const normalizeKey = (key) => {
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(key)) {
    const [d, m, y] = key.split(".")
    return `${y}-${m}-${d}`
  }
  return key
}

const normalizePriceMap = (map) => {
  if (!map || typeof map !== "object") return {}
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [normalizeKey(k), v]))
}

const useTransferSearch = () => {
  const [dates, setDates] = useState(defaultDates)
  const [passengers, setPassengers] = useState(defaultPassengers)
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY)
  const [searchParams, setSearchParams] = useState(null)
  const [calendarDatesData, setCalendarDatesData] = useState({
    available_depart: {},
    available_retur: {},
  })

  const fetchedMonthsRef = useRef(new Set())

  const [triggerSearch, { data, isLoading, isFetching, error }] = useLazySearchTransfersQuery()

  const [triggerReturnSearch] = useLazySearchTransfersQuery()
  const [triggerGetDates, { isFetching: isLoadingDates }] = useLazyGetTransferDatesQuery()
  const [prepareBooking] = useTransPrepareBookingMutation()

  const handleDatesChange = useCallback(({ from, to }) => {
    setDates({ depart: from ?? [], return: to ?? [] })
  }, [])

  const handlePassengersChange = useCallback((updated) => {
    setPassengers((prev) => ({ ...prev, ...updated }))
  }, [])

  const buildParams = useCallback(
    (selectedFrom, selectedTo, overrideCurrency) => ({
      from_type: selectedFrom.type,
      from_id: selectedFrom.id,
      to_type: selectedTo.type,
      to_id: selectedTo.id,
      depart_from: formatToApiDate(dates.depart[0]),
      depart_to: dates.depart.at(-1) ? formatToApiDate(dates.depart.at(-1)) : undefined,
      return_from: dates.return[0] ? formatToApiDate(dates.return[0]) : undefined,
      return_to: dates.return.at(-1) ? formatToApiDate(dates.return.at(-1)) : undefined,
      adults: passengers.adults,
      children: passengers.children,
      infants: passengers.infants,
      currency: overrideCurrency ?? currency,
    }),
    [dates, passengers, currency]
  )

  const runSearch = useCallback(
    (selectedFrom, selectedTo, overrideCurrency) => {
      if (!selectedFrom || !selectedTo) return
      if (!dates.depart[0]) return

      const params = buildParams(selectedFrom, selectedTo, overrideCurrency)
      setSearchParams(params)
      triggerSearch(params)
    },
    [dates, buildParams, triggerSearch]
  )

  // v2: поиск обратного трансфера, те же параметры что у основного поиска, но leg = "return"

  const searchReturn = useCallback(
    async (returnDate) => {
      if (!searchParams || !returnDate) return []

      const formatted = formatToApiDate(returnDate)
      const result = await triggerReturnSearch({
        ...searchParams,
        leg: "return",
        return_from: formatted,
        return_to: formatted,
      }).unwrap()

      return result?.transfers ?? []
    },
    [searchParams, triggerReturnSearch]
  )

  const handleCurrencyChange = useCallback(
    async (code, selectedFrom, selectedTo) => {
      setCurrency(code)
      fetchedMonthsRef.current = new Set()

      if (searchParams && selectedFrom && selectedTo) {
        const params = buildParams(selectedFrom, selectedTo, code)
        setSearchParams(params)
        triggerSearch(params)
      }
    },
    [buildParams, triggerSearch, searchParams]
  )

  const resetCalendarCache = useCallback(() => {
    fetchedMonthsRef.current = new Set()
    setCalendarDatesData({ available_depart: {}, available_retur: {} })
  }, [])

  const fetchCalendarDates = useCallback(
    async (selectedFrom, selectedTo, { year, month }) => {
      if (!selectedFrom || !selectedTo) return

      const cacheKey = `${selectedFrom.type}-${selectedFrom.id}__${selectedTo.type}-${selectedTo.id}__${year}-${month}__${currency}`

      if (fetchedMonthsRef.current.has(cacheKey)) return
      fetchedMonthsRef.current.add(cacheKey)

      try {
        const result = await triggerGetDates({
          from_type: selectedFrom.type,
          from_id: selectedFrom.id,
          to_type: selectedTo.type,
          to_id: selectedTo.id,
          adults: passengers.adults,
          children: passengers.children,
          infants: passengers.infants,
          year,
          month,
          currency,
        })

        if (result?.data) {
          setCalendarDatesData((prev) => ({
            available_depart: {
              ...prev.available_depart,
              ...normalizePriceMap(result.data.available_depart),
            },
            available_retur: {
              ...prev.available_retur,
              ...normalizePriceMap(result.data.available_retur),
            },
          }))
        }
      } catch (e) {
        fetchedMonthsRef.current.delete(cacheKey)
        console.error("fetchCalendarDates error:", e)
      }
    },
    [passengers, triggerGetDates, currency]
  )

  const handleBook = useCallback(
    async ({
      transfer_price_id,
      pickup_sign,
      additional_address,
      children_ages,
      comment,
      return_price_id,
      return_from,
    }) => {
      if (!searchParams) return
      try {
        const response = await prepareBooking({
          ...searchParams,
          transfer_price_id,
          comment,
          children_ages,
          pickup_sign,
          additional_address,
          // v2: явно выбранный обратный трансфер (дата из модалки перекрывает дату из формы)
          ...(return_price_id
            ? {
                return_price_id,
                return_from: formatToApiDate(return_from),
                return_to: formatToApiDate(return_from),
              }
            : {}),
        })
        const resp = response?.data
        const targetUrl = resp?.url || resp?.redirect
        if (targetUrl) {
          window.open(targetUrl, "_blank", "noopener,noreferrer")
        } else {
          console.warn("No redirect URL in response", resp)
        }
        return resp
      } catch (e) {
        console.error("Booking error:", e)
      }
    },
    [searchParams, prepareBooking]
  )

  const isRoundTrip = dates.return.length > 0
  const availableDepart = Object.keys(calendarDatesData.available_depart)
  const availableRetur = Object.keys(calendarDatesData.available_retur)

  // дата возврата из формы поиска в формате YYYY-MM-DD для <input type="date">
  const defaultReturnDate = dates.return[0] ? normalizeKey(formatToApiDate(dates.return[0])) : ""

  return {
    dates,
    handleDatesChange,
    isRoundTrip,
    passengers,
    handlePassengersChange,
    handleBook,
    runSearch,
    searchReturn,
    defaultReturnDate,
    fetchCalendarDates,
    resetCalendarCache,
    availableDepart,
    availableRetur,
    departPriceData: calendarDatesData.available_depart,
    returPriceData: calendarDatesData.available_retur,
    isLoadingDates,
    results: data,
    isSearching: isLoading || isFetching,
    searchError: error,
    currency,
    handleCurrencyChange,
  }
}

export default useTransferSearch
